import { PriceRange } from "@ui/components/PriceRange";
import { usdPriceScale } from "@ui/lib/price-display";
import { BUDGET_MAX_CENTS, BUDGET_MIN_CENTS, BUDGET_STEP_CENTS } from "../../../filter-values";

export interface BudgetFilterProps {
  /** Raw histogram counts over [BUDGET_MIN_CENTS, BUDGET_MAX_CENTS). */
  distribution?: number[];
  /** Monthly [min, max] budget in USD cents, or null for the full range. */
  onChange: (range: [number, number] | null) => void;
  value: [number, number] | null;
}

// A raw converted step lands thumbs on non-round display amounts (e.g. £39.50
// stops), so snap to the nearest 1/2/5 × 10^n.
function roundDisplayStep(raw: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  return [1, 2, 5, 10]
    .map((k) => k * magnitude)
    .reduce((best, candidate) => (Math.abs(candidate - raw) < Math.abs(best - raw) ? candidate : best));
}

// The slider edges mean "no bound on this side" rather than a number: for this
// corpus ten thousand dollars a month and no limit are the same answer, and a
// range that can only ever narrow gives a family no way back to the unfiltered
// ranking. State/URL/wire stay USD cents; only this control speaks the
// viewer's display currency (see `usdPriceScale`).
export function BudgetFilter({ distribution, onChange, value }: BudgetFilterProps) {
  const scale = usdPriceScale();

  const step = roundDisplayStep((BUDGET_STEP_CENTS / 100) * scale.rate);
  const displayMin = scale.centsToDisplayFloor(BUDGET_MIN_CENTS);
  // Step-aligned so the top thumb can actually land there — react-aria caps a
  // thumb at the last whole step from the minimum, and an unreachable top edge
  // would make the "no ceiling" reset impossible to drag back to. Overshooting
  // the converted maximum is harmless: the edge means "no bound" anyway.
  const displayMax = displayMin + Math.ceil((scale.centsToDisplayCeil(BUDGET_MAX_CENTS) - displayMin) / step) * step;
  const displayValue: [number, number] = value
    ? [
        Math.max(displayMin, scale.centsToDisplayFloor(value[0])),
        Math.min(displayMax, scale.centsToDisplayCeil(value[1])),
      ]
    : [displayMin, displayMax];

  const handleChange = (range: [number, number]) => {
    if (range[0] <= displayMin && range[1] >= displayMax) {
      onChange(null);
      return;
    }
    // An edge maps back to its exact cents bound so useRange recognises it as
    // "no bound on this side" regardless of conversion rounding.
    const minCents = range[0] <= displayMin ? BUDGET_MIN_CENTS : scale.displayToCentsMin(range[0]);
    const maxCents = range[1] >= displayMax ? BUDGET_MAX_CENTS : Math.max(minCents, scale.displayToCentsMax(range[1]));
    onChange([minCents, maxCents]);
  };

  return (
    <PriceRange
      currencySymbol={scale.symbol}
      distribution={distribution}
      max={displayMax}
      min={displayMin}
      onChange={handleChange}
      step={step}
      value={displayValue}
    />
  );
}
